import type { RuleSetPermissionMap } from './ruleSets';

/**
 * User Management's pure half (R8): the wire shapes of the admin service's `/api/users`, and
 * what the dialogs check before they send. The admin service decides (`dotnet/admin-api`,
 * `UserEndpoints` / `PasswordPolicy`); these mirror its rules so an operator sees a problem
 * before a round trip, not instead of one.
 *
 * `/api/*` is the admin service's own surface, not the engine's, so these types live here and
 * not in `packages/shared` — the same reason `api.ts` spells the BFF routes itself.
 */

export type UserRole = 'Viewer' | 'Editor' | 'Approver' | 'Admin';

/** In the order the admin service stores and returns them. */
export const USER_ROLES: readonly UserRole[] = ['Viewer', 'Editor', 'Approver', 'Admin'];

/** `GET /api/users` row — `UserView` in dotnet/admin-api. Never a password or its hash. */
export interface ConsoleUser {
  id: string;
  username: string;
  displayName: string;
  email?: string;
  roles: string[];
  disabled: boolean;
  mustChangePassword: boolean;
  locked: boolean;
  lockedUntil?: string;
  failedLogins: number;
  createdUtc: string;
  createdBy: string;
  updatedUtc: string;
  updatedBy: string;
  lastLoginUtc?: string;
  /** Sent back as `If-Match` on PATCH / DELETE so a concurrent edit is a 409, not a silent overwrite. */
  etag: string;
  // R8b per-rule-set permissions
  /** The stored map; absent = none yet (every set at the level the role allows). */
  ruleSetPermissions?: RuleSetPermissionMap | null;
  /** What the user may do on each set now (the admin service's computation); absent = not known. */
  effectiveRuleSetPermissions?: RuleSetPermissionMap | null;
}

export interface UserCreateRequest {
  username: string;
  displayName: string;
  email?: string;
  roles: UserRole[];
  temporaryPassword: string;
  /** R8b: absent = no map (the role decides on every set). */
  ruleSetPermissions?: RuleSetPermissionMap;
}

export interface UserUpdateRequest {
  displayName?: string;
  /** `null` clears it. */
  email?: string | null;
  roles?: UserRole[];
  disabled?: boolean;
  /** R8b: an object replaces the map whole; `null` removes it (back to the role on every set). */
  ruleSetPermissions?: RuleSetPermissionMap | null;
}

/** `GET /api/auth/providers`. Entra is configuration only for now: `configured` says whether a tenant is set. */
export interface AuthProviders {
  mode: string;
  local: { enabled: boolean };
  entra: { configured: boolean; signInImplemented: boolean };
}

export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 256;
export const DISPLAY_NAME_MAX = 100;
export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{1,63}$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** A problem the dialogs can name before sending. Each is an i18n key `users.problem.<code>`. */
export type UserFormProblem = 'username' | 'displayName' | 'email' | 'roles' | PasswordProblem;
export type PasswordProblem = 'short' | 'long' | 'username_password' | 'mismatch';

/** The admin service's password policy: 12–256 characters, and never the username. */
export function passwordProblems(password: string, username: string, confirm?: string): PasswordProblem[] {
  const out: PasswordProblem[] = [];
  if (password.length < PASSWORD_MIN) out.push('short');
  else if (password.length > PASSWORD_MAX) out.push('long');
  else if (username.trim() !== '' && password.trim().toLowerCase() === username.trim().toLowerCase()) out.push('username_password');
  if (confirm !== undefined && confirm !== password) out.push('mismatch');
  return out;
}

export interface UserForm {
  username: string;
  displayName: string;
  email: string;
  roles: UserRole[];
}

export function userFormProblems(form: UserForm, checkUsername: boolean): UserFormProblem[] {
  const out: UserFormProblem[] = [];
  if (checkUsername && !USERNAME_PATTERN.test(form.username.trim().toLowerCase())) out.push('username');
  const name = form.displayName.trim();
  if (name === '' || name.length > DISPLAY_NAME_MAX) out.push('displayName');
  const email = form.email.trim();
  if (email !== '' && (email.length > 254 || !EMAIL_PATTERN.test(email))) out.push('email');
  if (form.roles.length === 0) out.push('roles');
  return out;
}

/** The one status a row shows, most important first. Each is an i18n key `users.status.<code>`. */
export type UserStatus = 'disabled' | 'locked' | 'mustChange' | 'active';

export function userStatus(u: Pick<ConsoleUser, 'disabled' | 'locked' | 'mustChangePassword'>): UserStatus {
  if (u.disabled) return 'disabled';
  if (u.locked) return 'locked';
  if (u.mustChangePassword) return 'mustChange';
  return 'active';
}

/**
 * The admin service's refusals that an operator should read in words (409 guards and the
 * rest) → i18n key. Anything else shows the server's own message.
 */
const ERROR_KEYS: Record<string, string> = {
  cannot_delete_self: 'users.error.cannot_delete_self',
  cannot_disable_self: 'users.error.cannot_disable_self',
  cannot_remove_own_admin: 'users.error.cannot_remove_own_admin',
  last_admin: 'users.error.last_admin',
  user_exists: 'users.error.user_exists',
  etag_mismatch: 'users.error.etag_mismatch',
  if_match_required: 'users.error.etag_mismatch',
  user_not_found: 'users.error.user_not_found',
  current_password_wrong: 'users.password.error.current',
  password_unchanged: 'users.password.error.unchanged',
};

export function userErrorKey(code: string | undefined): string | null {
  return code ? (ERROR_KEYS[code] ?? null) : null;
}

/** Roles in the canonical order, known ones only. */
export function knownRoles(roles: readonly string[]): UserRole[] {
  return USER_ROLES.filter((r) => roles.some((x) => x.toLowerCase() === r.toLowerCase()));
}
